import { useCallback, useMemo, useState } from 'react'

import type { UseCollectionProductsOptions } from '@lib/collection/useCollectionProducts'
import useCollectionProducts from '@lib/collection/useCollectionProducts'
import type { GetCollectionProductsPerPageOptions } from '@lib/operations/collection'
import { PRODUCTS_PER_PAGE } from '@lib/operations/collection'
import type {
	Breadcrumbs,
	Collection,
	CollectionProducts,
	FilterValues,
	Filters,
	PageInfo,
	SortByOptions
} from '@lib/types'

import Spinner from '@components/Spinner'

import ProductsList from './ProductsList'
import QuickShopModal from './QuickShopModal'
import RefineResults from './RefineResults'

type Props = {
	collectionSlug: string
	products: CollectionProducts
	pageInfo: PageInfo
	breadcrumbs?: Breadcrumbs | null
	filtersOptions: Filters
	favorites?: string[] | null | undefined
	filterMenuOptions?: { title: string; path: string; active: boolean }[]
	isMemberMarket?: boolean
	quickShopProductIdSelected?: string | null
	setQuickShopProductIdSelected?: (id: string | null) => void
}

export default function ProductsListContainer({
	collectionSlug,
	products: initialProducts,
	pageInfo: initialPageInfo,
	breadcrumbs,
	filtersOptions,
	favorites,
	filterMenuOptions,
	isMemberMarket = false,
	quickShopProductIdSelected,
	setQuickShopProductIdSelected
}: Props) {
	const [queryOptions, setQueryOptions] = useState<UseCollectionProductsOptions>({
		endCursor: null,
		filters: [],
		sortBy: 'COLLECTION_DEFAULT',
		reverse: false
	})

	const [isMobileFiltersOpen, toggleMobileFilters] = useState(false)

	const currentSortByOptions = {
		sortBy: queryOptions.sortBy,
		reverse: queryOptions.reverse
	} as SortByOptions

	const { products, pageInfo, isLoading } = useCollectionProducts(collectionSlug, queryOptions, {
		products: initialProducts,
		pageInfo: initialPageInfo
	})

	const setFilters = (newFilters: FilterValues) => {
		setQueryOptions({
			...queryOptions,
			filters: newFilters,
			endCursor: null // reset pagination each time a new filter is set
		})
	}

	const setSortByOptions = (newSortByOptions: SortByOptions) => {
		setQueryOptions({
			...queryOptions,
			...newSortByOptions,
			endCursor: null // reset pagination each time sort by changes
		})
	}

	/**
	 * Set the current page end cursor for pagination. Shopify's API will use this
	 * cursor in GraphQL 'after' value, meaning 'get all products after <cursor>'
	 *
	 * @param newEndCursor - the cursor representing the last item of the current page
	 */
	const setPageEndCursor = useCallback(
		(newEndCursor: GetCollectionProductsPerPageOptions['endCursor']) => {
			setQueryOptions((prev) => ({
				...prev,
				endCursor: newEndCursor
			}))
		},
		[]
	)

	const counter: string = useMemo(() => {
		if (pageInfo && pageInfo.hasNextPage) {
			return `${PRODUCTS_PER_PAGE}+`
		}

		if (products && products.length > 0) {
			return products.length.toString()
		}

		return ''
	}, [products, pageInfo])

	return (
		// {/* TODO: fontawesome */}
		<section className="pt-0 section">
			<div className="container relative flex flex-col items-start gap-10 lg:flex-row">
				{/* Sidebar filters */}
				<RefineResults
					filterMenuOptions={filterMenuOptions}
					filters={queryOptions.filters}
					setFilters={setFilters}
					filtersOptions={filtersOptions}
					isMobileFiltersOpen={isMobileFiltersOpen}
					closeMobileFilters={() => toggleMobileFilters(false)}
					sortByOptions={currentSortByOptions}
					setSortByOptions={setSortByOptions}
				/>

				{/* Products grid */}
				<ProductsList
					counter={counter}
					products={products}
					breadcrumbs={breadcrumbs}
					pageInfo={pageInfo}
					isLoading={isLoading}
					sortByOptions={currentSortByOptions}
					setSortByOptions={setSortByOptions}
					setPageEndCursor={setPageEndCursor}
					favorites={favorites}
					openMobileFilters={() => toggleMobileFilters(true)}
					isMemberMarket={isMemberMarket}
					quickShopProductIdSelected={quickShopProductIdSelected}
					setQuickShopProductIdSelected={setQuickShopProductIdSelected}
				/>

				<QuickShopModal
					productId={quickShopProductIdSelected || null}
					onClose={() => setQuickShopProductIdSelected?.(null)}
					collectionProducts={products}
				/>

				{/* Full-screen loading overlay only for fresh loads (filter/sort resets).
				    Scroll pagination (non-null cursor) uses the inline spinner in ProductsList. */}
				{isLoading && queryOptions.endCursor === null && (
					<div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50 pointer-events-none">
						<Spinner />
					</div>
				)}
			</div>
		</section>
	)
}
